import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getResponse } from 'msw';
import { handlers } from './mocks';
import type { HomeScenario } from './homeScenarios';
import type { DevelopmentPlan, HomeReference, ProjectDetail, ProjectSummary, ShareProjectInput } from './types';

const API = '/api/plan-accueil-variantes';
const KEY = ['plan2cost', 'plan-accueil-variantes'] as const;

/** Transport MSW en mémoire : impossible de laisser passer une requête vers un serveur réel. */
export async function mockResponse(path: string, method = 'GET', body?: ShareProjectInput, viewer?: string) {
  const response = await getResponse(handlers, new Request(`${window.location.origin}${API}${path}`, {
    method, headers: { 'Content-Type': 'application/json', ...(viewer || body ? { 'X-Viewer': viewer ?? body?.profile ?? '' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  }));
  if (!response) throw new Error('Route de démonstration non enregistrée');
  return response;
}

export async function mockRequest<T>(path: string, method = 'GET', body?: ShareProjectInput): Promise<T> {
  const response = await mockResponse(path, method, body);
  const payload: unknown = await response.json();
  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload ? String(payload.message) : 'Requête impossible';
    throw new Error(`HTTP ${response.status} — ${message}`);
  }
  return payload as T;
}

export function useProjects(scenario: HomeScenario) {
  return useQuery({
    queryKey: [...KEY, 'projects', scenario],
    queryFn: () => mockRequest<ProjectSummary[]>(`/projects?scenario=${scenario}`), retry: false,
  });
}
export function useProject(id: string) {
  return useQuery({
    queryKey: [...KEY, 'project', id],
    queryFn: () => mockRequest<ProjectDetail>(`/projects/${id}`), retry: false,
  });
}
export function useReferential() {
  return useQuery({
    queryKey: [...KEY, 'referential', 'rennes'],
    queryFn: () => mockRequest<HomeReference>('/referential'), staleTime: Infinity, retry: false,
  });
}
export function useDevelopmentPlans(id: string) {
  return useQuery({
    queryKey: [...KEY, 'plans', id],
    queryFn: () => mockRequest<DevelopmentPlan[]>(`/projects/${id}/plans`), retry: false,
  });
}
export function useShareProject(id: string, action: 'invite' | 'update' = 'invite') {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ShareProjectInput) => mockRequest<ProjectSummary>(
      `/projects/${id}/sharing${action === 'update' ? `/${encodeURIComponent(input.name)}` : ''}`,
      action === 'update' ? 'PUT' : 'POST', input),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY }),
  });
}
export function useResetData() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => mockRequest('/reset', 'POST'),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY }),
  });
}
